import React, { useEffect, useMemo, useState } from 'react';
import { productService } from '../../services/productService';
import { vendorService } from '../../services/vendorService';

export const Category = () => {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [products, setProducts] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadVendors = async () => {
      setLoadingVendors(true);
      setError('');

      try {
        const response = await vendorService.getVendors();
        if (response?.data?.success) {
          const vendorList = response.data.data || [];
          setVendors(vendorList);
          if (!selectedVendor && vendorList.length) {
            setSelectedVendor(vendorList[0].id);
          }
        }
      } catch (err) {
        setError('Unable to load vendors.');
        console.error(err);
      } finally {
        setLoadingVendors(false);
      }
    };

    loadVendors();
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      setLoadingProducts(true);
      setError('');

      try {
        const params = selectedVendor ? { vendor_id: selectedVendor } : {};
        const response = await productService.getProducts(params);
        if (response?.data?.success) {
          setProducts(response.data.data || []);
        } else {
          setError(response?.data?.message || 'Unable to load products');
        }
      } catch (err) {
        setError('Unable to load products.');
        console.error(err);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, [selectedVendor]);

  const productsByCategory = useMemo(() => {
    return products.reduce((acc, product) => {
      const category = product.category_name || 'Uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push(product);
      return acc;
    }, {});
  }, [products]);

  const categoryKeys = Object.keys(productsByCategory);

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Vendor Products by Category</h1>
          <p className="text-gray-500 mt-1">
            Select a vendor and view products grouped by predefined categories.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="font-medium text-sm text-gray-700">Vendor</label>
          <select
            className="border rounded px-3 py-2 bg-white"
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
          >
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      {(loadingVendors || loadingProducts) && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-600">
          Loading data...
        </div>
      )}

      {!loadingProducts && !loadingVendors && !categoryKeys.length && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-600">
          No products found for the selected vendor.
        </div>
      )}

      {categoryKeys.map((category) => (
        <div key={category} className="mb-6 rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b px-5 py-4 bg-gray-50">
            <h2 className="text-xl font-semibold">{category}</h2>
            <p className="text-sm text-gray-500">{productsByCategory[category].length} product(s)</p>
          </div>
          <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3">
            {productsByCategory[category].map((product) => (
              <div key={product.id} className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{product.name}</h3>
                    <p className="text-sm text-gray-500">{product.category_name || 'Uncategorized'}</p>
                  </div>
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                    ₹{product.selling_price_with_gst || (product.variants?.length ? product.variants[0].price : product.price) || 'N/A'}
                  </span>
                </div>
                <p className="mt-3 text-sm text-gray-600 truncate">{product.description || 'No description available.'}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                  <span className={`rounded-full px-2 py-1 ${product.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {product.is_available ? 'Available' : 'Unavailable'}
                  </span>
                  <span className={`rounded-full px-2 py-1 ${product.is_veg ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {product.is_veg ? 'Veg' : 'Non-Veg'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
